/**
 * Variant filter picker
 *
 * Shop 原本（Dawn 默认）会把「当前组合不存在」的 variant 值渲染成 disabled 并划线。
 * 这个组件把两个 option 的产品改成 collection filter picker 的行为：
 *
 *   - 只在产品恰好有两个 option、且用 button（radio）类型时启用；
 *   - 页面打开时默认选中第一个 MODEL，并把 COLOR 默认选到该 MODEL 下第一个可用颜色
 *     （2026-11-03 决策；此前是「COLOR 不选」）；COLOR 已按该 MODEL 过滤，
 *     不可用的颜色直接隐藏（不是划线）；
 *   - 第一个 option 永远可选，不会被隐藏、禁用或划线；
 *   - 换 MODEL 时 COLOR 自动切到新 MODEL 下第一个可用颜色；
 *   - 两个 option 都选好之前，Add to cart / 动态结账按钮保持禁用；
 *   - 如果 URL 带 ?variant=xxx（分享链接 / 广告链接），按该 variant 预选。
 *
 * 服务端渲染的 disabled 状态仍然存在，这里只负责覆盖它：
 * 去掉 .disabled、改成 hidden，并在每次 section 重新渲染后重新套用。
 */
(() => {
  const HIDDEN_CLASS = 'variant-filter-picker-hidden';
  const instances = new WeakMap();
  let scheduled = false;
  // 选项状态按「产品 + section」记住，section 局部刷新 / 元素被替换后不丢
  const stateStore = new Map();

  class VariantFilterPicker {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.section;
      this.stateKey = `${root.dataset.productId || ''}:${root.dataset.section || ''}`;
      if (!stateStore.has(this.stateKey)) {
        stateStore.set(this.stateKey, { model: null, color: null, initialized: false });
      }
      this.state = stateStore.get(this.stateKey);
      this.variants = [];
      this.groups = [];
      this.picker = null;
      this.pendingLabel = '';
      this.started = false;

      this.refresh();
    }

    /* ---------- DOM 同步 ---------- */

    refresh(forceSync = false) {
      const picker = this.root.querySelector('variant-selects[data-filter-picker]');
      if (!picker) {
        this.picker = null;
        return;
      }

      const isNewPicker = picker !== this.picker;
      this.picker = picker;

      if (isNewPicker) {
        this.readVariants(picker);
        this.readGroups(picker);
        this.pendingLabel = picker.dataset.chooseOptions || '';

        if (!this.state.initialized) {
          this.state.initialized = true;
          this.applyInitialSelection();
        }
        forceSync = true;
      }

      if (this.groups.length === 2 && (forceSync || this.lastSignature !== this.signature())) {
        this.sync(forceSync);
      }
    }

    readVariants(picker) {
      this.variants = [];
      const node = picker.querySelector('script[data-filter-picker-variants]');
      if (!node) return;
      try {
        const parsed = JSON.parse(node.textContent);
        if (Array.isArray(parsed)) this.variants = parsed;
      } catch (error) {
        this.variants = [];
      }
    }

    readGroups(picker) {
      this.groups = [];
      const groups = Array.from(picker.querySelectorAll('.product-form__input')).filter((group) =>
        group.querySelector('input[type="radio"]')
      );
      groups.slice(0, 2).forEach((group, index) => {
        this.groups[index] = {
          index,
          element: group,
          inputs: Array.from(group.querySelectorAll('input[type="radio"]')),
        };
      });
    }

    applyInitialSelection() {
      // 1) 带 ?variant= 的分享链接：按该 variant 预选
      const params = new URLSearchParams(window.location.search);
      const variantId = params.get('variant');
      if (variantId) {
        const variant = this.variants.find((item) => String(item.id) === String(variantId));
        if (variant) {
          this.state.model = variant.option1;
          this.state.color = variant.option2;
          return;
        }
      }

      // 2) 默认：选中第一个 MODEL，并把 COLOR 默认选到该 MODEL 下第一个可用颜色
      const checkedModel = this.groups[0]?.inputs?.find((input) => input.checked);
      const firstModel = checkedModel || this.groups[0]?.inputs?.[0];
      if (firstModel) {
        this.state.model = firstModel.value;
        this.state.color = this.firstAvailableColor(firstModel.value);
      } else {
        this.state.color = null;
      }
    }

    signature() {
      return `${this.state.model ?? ''}|${this.state.color ?? ''}`;
    }

    sync(force = false) {
      const [modelGroup, colorGroup] = this.groups;
      if (!modelGroup || !colorGroup) return;
      this.lastSignature = this.signature();

      // 第一个 option（MODEL）永远可选、永远显示
      modelGroup.inputs.forEach((input) => {
        this.setVisible(input, true);
        input.checked = this.state.model === input.value;
      });

      // 第二个 option（COLOR）：选定 MODEL 后才过滤，不可用的直接隐藏
      const visibleColors = new Set();
      colorGroup.inputs.forEach((input) => {
        const visible = this.isColorAvailable(input.value);
        if (visible) visibleColors.add(input.value);
        this.setVisible(input, visible);
      });

      if (this.state.color && !visibleColors.has(this.state.color)) {
        this.state.color = null;
        this.lastSignature = this.signature();
      }

      colorGroup.inputs.forEach((input) => {
        input.checked = this.state.color !== null && input.value === this.state.color;
      });

      this.updateFormState();
    }

    setVisible(input, visible) {
      input.classList.remove('disabled');
      input.disabled = false;
      input.classList.toggle(HIDDEN_CLASS, !visible);
      if (visible) {
        input.removeAttribute('hidden');
        input.removeAttribute('aria-hidden');
      } else {
        input.setAttribute('hidden', 'hidden');
        input.setAttribute('aria-hidden', 'true');
      }
    }

    isColorAvailable(value, model = this.state.model) {
      if (!model) return true;
      return this.variants.some(
        (variant) => variant.option1 === model && variant.option2 === value && variant.available === true
      );
    }

    // 某个 MODEL 下「第一个可用颜色」——按页面上 COLOR 的渲染顺序取
    firstAvailableColor(model) {
      if (!model) return null;
      const colorInputs = this.groups[1]?.inputs || [];
      for (const input of colorInputs) {
        if (this.isColorAvailable(input.value, model)) return input.value;
      }
      return null;
    }

    selectedVariant() {
      const { model, color } = this.state;
      if (!model || !color) return null;
      return (
        this.variants.find((variant) => variant.option1 === model && variant.option2 === color) || null
      );
    }

    updateFormState() {
      const variant = this.selectedVariant();
      const productForm =
        this.root.querySelector('product-form') || document.querySelector('product-form');
      if (!productForm) return;

      const variantIdInput = productForm.querySelector('input[name="id"]');
      const submitButton = productForm.querySelector('[type="submit"]');
      const submitLabel = submitButton?.querySelector('span:not(.loading__spinner)');
      const dynamicCheckout =
        productForm.querySelector('.variant-filter-dynamic-checkout') ||
        productForm.querySelector('.shopify-payment-button') ||
        productForm.querySelector('shopify-accelerated-checkout');

      if (variant) {
        if (variantIdInput) variantIdInput.value = variant.id;
        if (submitButton) {
          submitButton.removeAttribute('disabled');
          submitButton.removeAttribute('aria-disabled');
        }
        if (submitLabel && this.pendingLabel) {
          const label = window.variantStrings?.addToCart || 'Add to cart';
          if (submitLabel.textContent !== label) submitLabel.textContent = label;
        }
        if (dynamicCheckout) dynamicCheckout.style.removeProperty('display');
      } else {
        if (variantIdInput) variantIdInput.value = '';
        if (submitButton) {
          submitButton.setAttribute('disabled', 'disabled');
          submitButton.setAttribute('aria-disabled', 'true');
        }
        if (submitLabel && this.pendingLabel && submitLabel.textContent !== this.pendingLabel) {
          submitLabel.textContent = this.pendingLabel;
        }
        if (dynamicCheckout) dynamicCheckout.style.setProperty('display', 'none');
        this.stripVariantFromUrl();
      }
    }

    stripVariantFromUrl() {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('variant')) return;
      url.searchParams.delete('variant');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    /* ---------- 交互 ---------- */

    handleChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      if (!this.picker || !this.picker.contains(input)) return;

      const group = this.groups.find((item) => item.element.contains(input));
      if (!group) return;

      if (group.index === 0) {
        // 换 MODEL：COLOR 自动切到新 MODEL 下第一个可用颜色
        this.state.model = input.value;
        this.state.color = this.firstAvailableColor(input.value);
        this.sync();
        return;
      }

      if (!this.state.model) {
        // 先选 COLOR 没有意义（MODEL 必须由用户自己定），把这个点击吃掉
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.sync();
        return;
      }

      this.state.color = input.value;
      this.sync();
    }
  }

  /* ---------- 页面级初始化 / section 重渲染后重新套用 ---------- */

  function getOrCreate(root) {
    let instance = instances.get(root);
    if (!instance) {
      instance = new VariantFilterPicker(root);
      instances.set(root, instance);
    }
    return instance;
  }

  function sweep() {
    document.querySelectorAll('product-info').forEach((root) => {
      const picker = root.querySelector('variant-selects[data-filter-picker]');
      const instance = instances.get(root);

      if (!picker) {
        // section 局部刷新期间 picker 可能暂时不存在，状态保留
        if (instance) instance.picker = null;
        return;
      }

      if (!instance) {
        getOrCreate(root);
      } else if (instance.picker !== picker) {
        instance.refresh(true);
      }
    });
  }

  // 只有「variant-selects 被换掉 / 出现新的 product-info」才需要重新同步，
  // 其它 DOM 变化（图片懒加载、轮播等）直接忽略，避免每次变更都重跑一遍。
  function needsWork() {
    const roots = document.querySelectorAll('product-info');
    for (const root of roots) {
      const picker = root.querySelector('variant-selects[data-filter-picker]');
      if (!picker) continue;
      const instance = instances.get(root);
      if (!instance || instance.picker !== picker) return true;
    }
    return false;
  }

  function schedule() {
    if (scheduled || !needsWork()) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      sweep();
    });
  }

  document.addEventListener(
    'change',
    (event) => {
      document.querySelectorAll('product-info').forEach((root) => {
        if (!root.contains(event.target)) return;
        getOrCreate(root).handleChange(event);
      });
    },
    true
  );

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
})();
